import * as React from 'react';
import {
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
  Platform,
  ColorValue,
  View as ViewRN,
  LayoutChangeEvent,
} from 'react-native';
import {images} from '../../../assets/icons';
import {Image} from '../../../shared/components/image';
import {useTheme} from '../../../shared/theme';
import {stripHTML} from '../../../shared/pipes';
import {useAlign, AlignFunc, DeviceSize} from '../../../shared/theme/helpers';
import {Theme} from '../../../shared/theme/interface';
import {useStyles} from '../../../shared/hooks/styles';
import {ProductResponsePayload} from '../../../shared/models/api/productResponsePayload';
import {Settings} from '../../../shared/constants/config';
import {useSelector} from 'react-redux';
import {themeSelector} from '../../../redux/selectors/runtime/theme';
import {VisImpStatus} from '../../../shared/models/visuallyImpairedSettings';

const GetStyles = (theme: Theme, headerBlockSize: number, align: AlignFunc) => {
  return useStyles(({mediaOnly}) => {
    return {
      container: {
        width: 280,
        maxHeight: 345,
        height: 345,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.color.borderColor,
        backgroundColor: theme.color.primaryNonGreyBackground,
        flexDirection: 'column',
        alignItems: 'stretch',
        shadowColor: theme.color.primaryBlackColor,
        shadowOpacity: 0.05,
        shadowOffset: {width: 0, height: 4},
        shadowRadius: 10,
        elevation: 5,
      },
      header: {
        height: headerBlockSize,
        position: 'relative',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        marginBottom: 20,
      },
      headerImageBlock: {
        position: 'absolute',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: headerBlockSize - 50,
      },
      logo: {
        position: 'relative',
        zIndex: 1,
        width: align(166),
        height: align(166),
      },
      body: {
        flexDirection: 'column',
        justifyContent: 'space-between',
        marginBottom: 10,
      },
      nameContainer: {alignItems: 'center', paddingHorizontal: 20, height: 'auto'},
      name: {
        ...mediaOnly(DeviceSize.desktop, {...theme.fonts.h6}, theme.fonts.h6),
        fontWeight: 'bold',
        color: theme.color.textColor,
        textAlign: 'center',
        height: 'auto',
      },
      categoryContainer: {
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        height: 40,
        marginBottom: 10,
        backgroundColor: 'rgba(0, 150, 90, 0.55)',
      },
      categoryText: {
        margin: 'auto',
        textAlign: 'center',
        ...mediaOnly(DeviceSize.desktop, theme.fonts.body, theme.fonts.body),
        color: theme.color.primaryNonGreyBackground,
      },
      descriptionPriceContainer: {
        width: '100%',
        height: 'auto',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap-reverse',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        marginTop: 20,
      },
      descriptionContainer: {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 'auto',
        ...mediaOnly(DeviceSize.desktop, theme.fonts.body, theme.fonts.body),
        color: theme.color.labelText,
        marginRight: 5,
      },
      description: {
        ...mediaOnly(DeviceSize.desktop, theme.fonts.body, theme.fonts.body),
        color: theme.color.labelText,
        flexBasis: 'auto',
      },
      priceContainer: {
        flexGrow: 0,
        flexShrink: 0,
        flexBasis: 'auto',
      },
      price: {
        ...mediaOnly(DeviceSize.desktop, theme.fonts.h6, theme.fonts.h6),
        color: theme.color.primaryColor,
        fontWeight: 'bold',
        marginBottom: 5,
      },
    };
  });
};

interface Props {
  product: ProductResponsePayload;
  style?: StyleProp<ViewStyle>;
  onPress?: () => void;
  nameRows: number;
  shortDescriptionRows: number;
  flexHeight: boolean;
}

const HEADER_BLOCK_SIZE = 130;

function _ProductCard({
  product: {id, name, departmentShortName: shortDescription, productImgUrl, price, categoryName},
  style,
  onPress,
  nameRows,
  shortDescriptionRows,
  flexHeight,
}: Props) {
  const settings = Settings.getSettings();
  const theme = useTheme();
  const align = useAlign();
  const headerBlockSize = align(HEADER_BLOCK_SIZE);
  const themeSettings = useSelector(themeSelector());
  const visuallyImpairedActive = themeSettings.visuallyImpaired === VisImpStatus.Active;
  const styles = GetStyles(theme, headerBlockSize, align);

  const memoisedStyles = React.useMemo(() => styles, []);

  const productImageUrl = React.useMemo(() => {
    return productImgUrl?.startsWith('http')
      ? productImgUrl
      : `${settings.baseUrl}Market/DownloadFile/${productImgUrl}`;
  }, [productImgUrl]);

  const [nameHeight, setNameHeight] = React.useState({
    parentHeight: 0,
  });
  const [descriptionHeight, setDescriptionHeight] = React.useState({
    parentHeight: 0,
  });

  const [isWrapDescription, setIsWrapDescription] = React.useState(false);

  const handleLayoutName = (e: LayoutChangeEvent) => {
    const {height} = e.nativeEvent.layout;

    setNameHeight({
      parentHeight: height,
    });
  };
  const handleLayoutDescription = (e: LayoutChangeEvent) => {
    const {height, width} = e.nativeEvent.layout;
    if (width < 150) {
      setIsWrapDescription(true);
    }

    setDescriptionHeight({
      parentHeight: height,
    });
  };

  const nameContainerHeight = flexHeight ? 'auto' : nameRows * theme.fonts.h6.fontSize!;
  const shortDescriptionHeight = flexHeight ? 'auto' : shortDescriptionRows * theme.fonts.h6.fontSize!;

  return (
    <Container
      testID={`product-item-${id}-id`}
      onPress={onPress}
      style={[memoisedStyles.container, {...(flexHeight ? {height: 'auto', maxHeight: 'auto'} : {})}]}>
      <View
        testID={'categoryContainer'}
        style={[memoisedStyles.categoryContainer, visuallyImpairedActive && {backgroundColor: 'grey'}]}>
        <Text testID={`product-item-${id}-description-id`} style={memoisedStyles.categoryText} numberOfLines={3}>
          {stripHTML(String(categoryName))}
        </Text>
      </View>
      <View testID={`product-item-${id}-header-id`} style={memoisedStyles.header}>
        <View testID={`product-item-${id}-logo-id`} style={memoisedStyles.headerImageBlock}>
          <Image style={memoisedStyles.logo} source={{uri: productImageUrl}} resizeMode='contain' />
        </View>
      </View>
      <View
        testID={`product-item-${id}-body-id`}
        style={[memoisedStyles.body, {...(flexHeight ? {marginBottom: 20} : {})}]}>
        <View
          style={[memoisedStyles.nameContainer, {height: nameContainerHeight}]}
          testID={`nameContainer`}
          onLayout={handleLayoutName}>
          <HoverableProductInfoItem
            infoItem={name!}
            itemStyle={memoisedStyles.name}
            lines={nameRows}
            dimensions={nameHeight}
          />
        </View>
        <View
          testID={`descriptionPriceContainer`}
          style={[
            memoisedStyles.descriptionPriceContainer,
            {...(isWrapDescription ? {flexWrap: 'wrap-reverse'} : {flexWrap: 'nowrap'})},
          ]}>
          <View
            style={[styles.descriptionContainer, {height: shortDescriptionHeight}]}
            onLayout={handleLayoutDescription}>
            <HoverableProductInfoItem
              infoItem={stripHTML(shortDescription)}
              itemStyle={memoisedStyles.description}
              lines={shortDescriptionRows}
              dimensions={descriptionHeight}
            />
          </View>
          <View style={[styles.priceContainer, {...(isWrapDescription ? {marginLeft: 'auto'} : {})}]}>
            <Text testID={`product-item-${id}-description-id`} style={memoisedStyles.price}>
              {`${stripHTML(String(price))} Б`}
            </Text>
          </View>
        </View>
      </View>
    </Container>
  );
}

type HoverableProductInfoItemProps = {
  itemStyle: StyleProp<ViewStyle>;
  infoItem: string;
  lines: number;
  dimensions: {
    parentHeight: number;
  };
};

const HoverableProductInfoItem = React.memo((props: HoverableProductInfoItemProps) => {
  const [isTextTrimmed, setIsTextTrimmed] = React.useState(false);
  const textRef = React.useRef<Text>(null);

  const [showTooltip, setShowTooltip] = React.useState(false);

  const handleMouseEnter = React.useCallback(() => {
    if (Platform.OS === 'web') {
      setShowTooltip(true);
    }
  }, [showTooltip]);

  const handleMouseLeave = React.useCallback(() => {
    if (Platform.OS === 'web') {
      setShowTooltip(false);
    }
  }, [showTooltip]);

  React.useLayoutEffect(() => {
    if (textRef.current) {
      textRef.current.measure((x, y, textWidth, textHeight, pageX, pageY) => {
        if (textHeight > props.dimensions.parentHeight) {
          setIsTextTrimmed(true);
        } else {
          setIsTextTrimmed(false);
        }
      });
    }
  }, [props.dimensions.parentHeight]);

  return (
    <>
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {showTooltip && isTextTrimmed ? (
          <View style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
            <View
              style={{
                position: 'absolute',
                bottom: 10,
                padding: 10,
                backgroundColor: 'white',
                borderRadius: 5,
                borderColor: 'black',
                borderWidth: 1,
                zIndex: 1,
              }}>
              <Text>{props.infoItem}</Text>
            </View>
            <View
              style={{
                position: 'absolute',
                bottom: 0,
                width: 0,
                height: 0,
                borderLeftWidth: 10,
                borderRightWidth: 10,
                borderTopWidth: 10,
                borderStyle: 'solid',
                backgroundColor: 'transparent',
                borderLeftColor: 'transparent',
                borderRightColor: 'transparent',
                borderTopColor: 'black',
                zIndex: 1,
              }}
            />
          </View>
        ) : (
          <></>
        )}
        <Text style={props.itemStyle} numberOfLines={props.lines} ref={textRef}>
          {props.infoItem}
        </Text>
      </div>
    </>
  );
});

const CardHeaderShape: React.FC<{color: ColorValue | undefined; size: number}> = (props) => {
  if (Platform.OS === 'web') {
    const style: ViewStyle = {
      width: '100%',
      height: '100%',
      position: 'absolute',
      left: 0,
      bottom: 0,
      zIndex: 0,
    };
    return <images.LoyaltyAcceptorCardTracery style={style} color={props.color} />;
  }
  const scale = 2;
  const style: ViewStyle = {
    width: props.size,
    height: props.size,
    backgroundColor: props.color,
    borderRadius: props.size,
    top: -(props.size * scale - props.size) * 0.5,
    transform: [{scale}],
  };
  return <View style={style} />;
};

interface ContainerProps {
  testID: any;
  children: React.ReactFragment;
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
  useHoverEffect?: boolean;
}

const Container = React.memo((props: ContainerProps) => {
  const {children, onPress, useHoverEffect, style, testID} = props;

  if (onPress) {
    return (
      <TouchableOpacity
        testID={testID}
        activeOpacity={Platform.OS === 'web' ? 1 : 0.5}
        onPress={onPress}
        style={style}
        {...{dataSet: {class: useHoverEffect ? 'product-card' : 'product-card--noHover'}}}>
        {children}
      </TouchableOpacity>
    );
  }

  return <View style={style}>{children}</View>;
});

export const ProductCard = React.memo(_ProductCard);
